import { EnvironmentBanner, Iconify, Logo } from '@btsis/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router';
import { api, toApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export function LoginPage() {
  const { t } = useTranslation();
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const branding = useQuery({
    queryKey: ['organization-branding'],
    queryFn: () => api.organization.branding({}),
    retry: false,
    staleTime: 240000,
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorCode(null);
    setSubmitting(true);
    try {
      const result = await login({ email, password });
      if (result.status === 'two_factor_required') {
        setChallengeToken(result.challengeToken);
        setCode('');
        return;
      }
      void navigate('/', { replace: true });
    } catch (error) {
      setErrorCode(toApiError(error).code);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTwoFactor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!challengeToken) return;
    setErrorCode(null);
    setSubmitting(true);
    try {
      await verifyTwoFactor({ challengeToken, code });
      void navigate('/', { replace: true });
    } catch (error) {
      setErrorCode(toApiError(error).code);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.primary.lighter} 100%)`,
      }}
    >
      <EnvironmentBanner floating />
      <Card sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 5 } }}>
        {challengeToken ? (
          <Stack spacing={3} component="form" onSubmit={handleTwoFactor} noValidate>
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Iconify
                icon="solar:shield-keyhole-bold-duotone"
                width={48}
                sx={{ color: 'primary.main' }}
              />
              <Typography variant="h4">{t('auth.twoFactor.title')}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('auth.twoFactor.subtitle')}
              </Typography>
            </Stack>

            {errorCode && (
              <Alert severity="error" role="alert">
                {t(`error.${errorCode}`, { defaultValue: t('error.INTERNAL_SERVER_ERROR') })}
              </Alert>
            )}

            <TextField
              label={t('auth.twoFactor.code')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoFocus
              fullWidth
              autoComplete="one-time-code"
              helperText={t('auth.twoFactor.recoveryHint')}
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 16 } }}
            />

            <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
              {submitting ? t('auth.login.submitting') : t('auth.twoFactor.submit')}
            </Button>
            <Button
              color="inherit"
              onClick={() => {
                setChallengeToken(null);
                setErrorCode(null);
              }}
            >
              {t('auth.twoFactor.back')}
            </Button>
          </Stack>
        ) : (
          <Stack spacing={3} component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              {branding.data?.logoUrl ? (
                <Box
                  component="img"
                  src={branding.data.logoUrl}
                  alt={branding.data.organizationName ?? ''}
                  sx={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain' }}
                />
              ) : (
                <Logo width={48} showText={false} />
              )}
              {branding.data && (
                <Typography variant="h6">{branding.data.organizationName}</Typography>
              )}
              <Typography variant="h4">
                {t('auth.login.title', { appName: t('common.appName') })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('auth.login.subtitle')}
              </Typography>
            </Stack>

            {errorCode && (
              <Alert severity="error" role="alert">
                {t(`error.${errorCode}`, { defaultValue: t('error.INTERNAL_SERVER_ERROR') })}
              </Alert>
            )}

            <TextField
              label={t('auth.login.email')}
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
              fullWidth
            />

            <TextField
              label={t('auth.login.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((value) => !value)}
                        edge="end"
                        aria-label={
                          showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')
                        }
                      >
                        <Iconify icon={showPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
              {submitting ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>
            <Button component={RouterLink} to="/forgot-password" color="inherit" size="small">
              {t('auth.forgot.link')}
            </Button>
          </Stack>
        )}
      </Card>
    </Box>
  );
}
